import PropTypes from 'prop-types'
import styled from 'astroturf/react'
import { PaymentAmount } from '@calendly/booking-kit'
const colorTextSuccess = 'var(--colorTextSuccess, var(--coreColorGreen800))'
const colorTextWarning = 'var(--colorTextWarning, var(--coreColorOrange700))'
import { IconButton } from '@calendly/ui/components/icon-button'
import { CloseMiniIcon } from '@calendly/ui/components/icons'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'

import { useValidateCouponMutation } from 'root-api'
import Info from 'components/enter_details/payment/info'
import { useI18n } from 'locales'
import { useTrackCommerce } from 'hooks/useTrackCommerce'
import { anonymousIdSelector } from 'selectors/segment'
import { useEventType } from 'components/use_event_type'
import { couponErrorSelector } from 'selectors/payment'
import { toLowerCase } from 'utils/strings'

import { couponErrorAnalyticsText } from './coupon'
import _Spacing from "info_coupon-Spacing.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=Spacing";
import _LineItem from "info_coupon-LineItem.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=LineItem";
import _BonusText from "info_coupon-BonusText.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=BonusText";
import _LimitedCouponText from "info_coupon-LimitedCouponText.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=LimitedCouponText";
import _CouponAmountContainer from "info_coupon-CouponAmountContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=CouponAmountContainer";
import _RemoveCouponButton from "info_coupon-RemoveCouponButton.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/info_coupon.js?styleId=RemoveCouponButton";


const MINIMUM_CHARGE_AMOUNT = 0.50

const Spacing = /*#__PURE__*/styled('div', null, {
  displayName: "Spacing",
  styles: _Spacing
})

const LineItem = /*#__PURE__*/styled('div', null, {
  displayName: "LineItem",
  styles: _LineItem
})

const BonusText = /*#__PURE__*/styled('span', null, {
  displayName: "BonusText",
  styles: _BonusText
})

const LimitedCouponText = /*#__PURE__*/styled('span', null, {
  displayName: "LimitedCouponText",
  styles: _LimitedCouponText
})

const CouponAmountContainer = /*#__PURE__*/styled('div', null, {
  displayName: "CouponAmountContainer",
  styles: _CouponAmountContainer
})

const RemoveCouponButton = /*#__PURE__*/styled(IconButton, null, {
  displayName: "RemoveCouponButton",
  styles: _RemoveCouponButton,
  attrs: props => ({
    ...props,
    children: <CloseMiniIcon />,
    size: 'small'
  })
})

const parseAmountWithSymbol = (amountWithSymbol) => {
  const symbol = amountWithSymbol[0]
  const amount = Number(amountWithSymbol.replace(symbol, ''))
  return { symbol, amount }
}

const couponFinalAmount = (coupon, amountWithSymbol, currency) => {
  if (!coupon) return amountWithSymbol
  const { symbol, amount } = parseAmountWithSymbol(amountWithSymbol)

  if (coupon.fixed_amount_cents) {
    const discountedAmount = amount - coupon.fixed_amount_cents / 100
    // If discount results in $0 or negative, return $0 (no minimum charge applied)
    if (discountedAmount <= 0) {
      return `${symbol}0.00`
    }
    // Apply minimum charge only if discounted amount is positive but below minimum
    const finalAmount = Math.max(discountedAmount, MINIMUM_CHARGE_AMOUNT)
    return `${symbol}${finalAmount.toFixed(2)}`
  }
  if (coupon.percent_off) {
    const discountedAmount = amount * (1 - coupon.percent_off / 100)
    // If discount results in $0 or negative, return $0 (no minimum charge applied)
    if (discountedAmount <= 0) {
      return `${symbol}0.00`
    }
    // Apply minimum charge only if discounted amount is positive but below minimum
    const finalAmount = Math.max(discountedAmount, MINIMUM_CHARGE_AMOUNT)
    return `${symbol}${finalAmount.toFixed(2)}`
  }
  return amountWithSymbol
}

const couponValueText = (coupon, amountWithSymbol, i18n) => {
  if (!coupon || !amountWithSymbol) return undefined
  const { symbol } = parseAmountWithSymbol(amountWithSymbol)

  if (coupon.fixed_amount_cents) {
    return `${symbol}${(coupon.fixed_amount_cents / 100).toFixed(2)} ${i18n.t('booking.templates.payment.coupon.off')}`
  }
  if (coupon.percent_off) {
    return `${(coupon.percent_off).toFixed(0)}% ${i18n.t('booking.templates.payment.coupon.off')}`
  }
  return undefined
}

const limitedCouponValue = (finalAmount, currency, i18n) => {
  const { amount, symbol } = parseAmountWithSymbol(finalAmount)
  // Don't show limited coupon warning for $0 amounts
  if (amount === 0) {
    return undefined
  }
  if (amount === MINIMUM_CHARGE_AMOUNT) {
    return i18n.t('booking.templates.payment.coupon.limited', { min_charge_amount: `${MINIMUM_CHARGE_AMOUNT.toFixed(2)}`, symbol })
  }
  return undefined
}

const InfoCoupon = ({
  amountWithSymbol,
  currency,
  onRemoveCoupon,
  paymentTerms = null
}) => {
  const { i18n } = useI18n()
  const [, { data: {
    resource: coupon
  }, reset }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })
  const track = useTrackCommerce()
  const anonymousId = useSelector(anonymousIdSelector)
  const couponSubmissionError = useSelector(couponErrorSelector)
  const eventType = useEventType()

  useEffect(() => {
    if (couponSubmissionError.error) {
      track('Booking Code Interaction',
        {
          invitee_anonymous_id: anonymousId,
          event_type_uuid: eventType.uuid,
          action: 'apply',
          booking_code_valid: false,
          booking_code: coupon.booking_code,
          discount_type: toLowerCase(coupon.booking_code_type),
          fixed_amount_cents: coupon.fixed_amount_cents,
          percent_off: coupon.percent_off,
          currency: coupon.currency,
          error_reason: couponErrorAnalyticsText(couponSubmissionError.data),
        })
      reset()
      onRemoveCoupon()
    }
  }, [couponSubmissionError, track, anonymousId, eventType, coupon, onRemoveCoupon, reset])

  if (!coupon) {
    return (
      <Info
        amountWithSymbol={amountWithSymbol}
        currency={currency}
        paymentTerms={paymentTerms}
      />
    )
  }


  const removeCoupon = () => {
    track('Booking Code Interaction',
      {
        invitee_anonymous_id: anonymousId,
        event_type_uuid: eventType.uuid,
        action: 'remove',
        booking_code_valid: null,
        booking_code: coupon.booking_code,
        discount_type: toLowerCase(coupon.booking_code_type),
        fixed_amount_cents: coupon.fixed_amount_cents,
        percent_off: coupon.percent_off,
        currency: coupon.currency,
      })
    reset()
    onRemoveCoupon()
  }

  return (
    <Spacing>
      <LineItem>
        {i18n.t('booking.templates.payment.coupon.original_price')}
        <PaymentAmount
          amount={amountWithSymbol}
          currencyCode={currency}
        />
      </LineItem>
      <LineItem>
        <BonusText>{`${coupon.description} (${coupon.code})`}</BonusText>
        <CouponAmountContainer>
          <BonusText>{couponValueText(coupon, amountWithSymbol, i18n)}</BonusText>
          <RemoveCouponButton
            aria-label={i18n.t('action.remove')}
            onClick={removeCoupon}
          />
        </CouponAmountContainer>
      </LineItem>
      {limitedCouponValue(couponFinalAmount(coupon, amountWithSymbol, currency), currency, i18n) && (
        <LineItem>
          <LimitedCouponText>{limitedCouponValue(couponFinalAmount(coupon, amountWithSymbol, currency), currency, i18n)}</LimitedCouponText>
        </LineItem>
      )}
      <LineItem>
        <strong>{i18n.t('booking.templates.payment.coupon.final_price')}</strong>
        <PaymentAmount
          amount={couponFinalAmount(coupon, amountWithSymbol, currency)}
          currencyCode={currency}
        />
      </LineItem>
      {paymentTerms && (
        <Spacing>
          <strong>{i18n.t('booking.templates.payment.payment_terms')}</strong>
          <div>{paymentTerms}</div>
        </Spacing>
      )}
    </Spacing>
  )
}

InfoCoupon.propTypes = {
  amountWithSymbol: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  paymentTerms: PropTypes.string,
  onRemoveCoupon: PropTypes.func.isRequired,
}

export default InfoCoupon
